import { useQuery } from '@tanstack/react-query';
import { Landmark, TrendingUp, TrendingDown, Calculator } from 'lucide-react';
import type { Fundamentals } from '@invest-copilot/shared';
import { Term, TERMS } from './Term';
import type { ReactNode } from 'react';
import { isDelistedError, DELISTED_MESSAGE } from '../lib/error-utils';
import { fetchJSON as getJSON } from '../lib/api-client';

export function FundamentalsCard({ symbol }: { symbol: string }) {
  const q = useQuery({
    queryKey: ['fundamentals', symbol],
    queryFn: () => getJSON<Fundamentals>(`/api/fundamentals/${encodeURIComponent(symbol)}`),
    enabled: !!symbol,
    staleTime: 30 * 60_000, // 30 min cache
    refetchOnMount: false,
    retry: 2,
    retryDelay: (i) => 500 * (i + 1),
  });

  if (q.isLoading) {
    return (
      <div className="card">
        <div className="text-(--text-muted) text-sm py-4 text-center">載入基本面…</div>
      </div>
    );
  }
  if (q.error) {
    const err = q.error as Error;
    return (
      <div className="card">
        <div className={isDelistedError(err) ? 'text-(--text-muted) text-sm' : 'text-(--up) text-sm'}>{isDelistedError(err) ? DELISTED_MESSAGE : `無法取得基本面：${err.message}`}</div>
      </div>
    );
  }
  if (!q.data) return null;

  const f = q.data;
  const hasAny = f.marketCap || f.trailingPE || f.trailingEps || f.dividendYield;
  if (!hasAny) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Landmark size={16} className="text-(--accent)" />
          <div className="font-bold text-(--text-primary) text-sm">基本面</div>
        </div>
        <div className="text-(--text-muted) text-xs">此標的無基本面資料</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Landmark size={16} className="text-(--accent)" />
          <div>
            <div className="font-bold text-(--text-primary) text-sm">基本面</div>
            <div className="text-[10px] text-(--text-muted)">
              {f.currency ?? ''}{f.name ? ` · ${f.name}` : ''}
            </div>
          </div>
        </div>
        {f.recommendationKey && (
          <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border ${
            recommendationCls(f.recommendationMean)
          }`}>
            分析師 {f.recommendationKey}{f.numberOfAnalystOpinions != null ? ` (${f.numberOfAnalystOpinions})` : ''}
          </span>
        )}
      </div>

      {/* === 估值 === */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-3">
        <Stat label={<Term tip={TERMS.MarketCap}>市值</Term>} value={fmtBig(f.marketCap)} />
        <Stat label={<Term tip={TERMS.PE}>P/E (TTM)</Term>} value={fmtNum(f.trailingPE, 1)} accent={peAccent(f.trailingPE)} />
        <Stat label={<Term tip={TERMS.ForwardPE}>P/E (Fwd)</Term>} value={fmtNum(f.forwardPE, 1)} />
        <Stat label={<Term tip={TERMS.PB}>P/B</Term>} value={fmtNum(f.priceToBook, 2)} />
        <Stat label={<Term tip={TERMS.EPS}>EPS (TTM)</Term>} value={fmtNum(f.trailingEps, 2)} />
        <Stat label={<Term tip={TERMS.EPS + ' 這裡是分析師預估的下一年 EPS。'}>EPS (Fwd)</Term>} value={fmtNum(f.forwardEps, 2)} />
        <Stat label={<Term tip={TERMS.DividendYield}>股息率</Term>} value={fmtPct(f.dividendYield)} accent={f.dividendYield && f.dividendYield > 0.03 ? 'up' : 'flat'} />
        <Stat label="配息率" value={fmtPct(f.payoutRatio)} />
      </div>

      {/* === 獲利能力 === */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-3">
        <Stat label={<Term tip={TERMS.GrossMargin}>毛利率</Term>} value={fmtPct(f.grossMargin)} accent={marginAccent(f.grossMargin, 0.3)} />
        <Stat label={<Term tip={TERMS.OperatingMargin}>營益率</Term>} value={fmtPct(f.operatingMargin)} accent={marginAccent(f.operatingMargin, 0.15)} />
        <Stat label={<Term tip={TERMS.ProfitMargin}>淨利率</Term>} value={fmtPct(f.profitMargin)} accent={marginAccent(f.profitMargin, 0.1)} />
        <Stat label={<Term tip={TERMS.ROE}>ROE</Term>} value={fmtPct(f.returnOnEquity)} accent={marginAccent(f.returnOnEquity, 0.15)} />
        <Stat label={<Term tip={TERMS.ROA}>ROA</Term>} value={fmtPct(f.returnOnAssets)} />
        <Stat label="EPS YoY" value={fmtPct(f.epsGrowthYoY)} accent={growthAccent(f.epsGrowthYoY)} />
        <Stat label="營收 YoY" value={fmtPct(f.revenueGrowthYoY)} accent={growthAccent(f.revenueGrowthYoY)} />
        <Stat label="EV" value={fmtBig(f.enterpriseValue)} />
      </div>

      {/* === 分析師目標價 === */}
      {f.targetMeanPrice != null && (
        <div className="p-2 rounded bg-(--bg-base) border border-(--border) mb-3">
          <div className="text-[10px] uppercase tracking-wider text-(--text-muted) mb-1 flex items-center gap-1">
            <Calculator className="h-3 w-3 text-(--accent)" />分析師目標價
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-lg font-bold tnum text-(--accent)">{f.targetMeanPrice.toFixed(2)}</div>
            <div className="text-[11px] text-(--text-muted) tnum">
              區間 {f.targetLowPrice?.toFixed(2) ?? '—'} ~ {f.targetHighPrice?.toFixed(2) ?? '—'}
            </div>
          </div>
        </div>
      )}

      {/* === Earnings history === */}
      {f.earningsHistory && f.earningsHistory.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-(--text-muted) mb-1">近 4 季 EPS</div>
          <table className="w-full text-[11px] tnum">
            <thead>
              <tr className="text-(--text-muted) text-left border-b border-(--border)">
                <th className="py-0.5 pr-2">季別</th>
                <th className="py-0.5 pr-2 text-right">實際</th>
                <th className="py-0.5 pr-2 text-right">預估</th>
                <th className="py-0.5 pr-2 text-right">驚喜</th>
              </tr>
            </thead>
            <tbody>
              {f.earningsHistory.map((e, i) => (
                <tr key={i}>
                  <td className="py-0.5 pr-2">{e.quarter}</td>
                  <td className="py-0.5 pr-2 text-right">{fmtNum(e.epsActual, 2)}</td>
                  <td className="py-0.5 pr-2 text-right text-(--text-muted)">{fmtNum(e.epsEstimate, 2)}</td>
                  <td className={`py-0.5 pr-2 text-right ${e.surprisePercent != null && e.surprisePercent > 0 ? 'text-(--up)' : e.surprisePercent != null && e.surprisePercent < 0 ? 'text-(--down)' : ''}`}>
                    {fmtPct(e.surprisePercent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* === 營收歷史 === */}
      {f.annualRevenue && f.annualRevenue.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-(--text-muted) mb-1">近年營收</div>
          <div className="flex items-end gap-1 h-12">
            {f.annualRevenue.slice().reverse().map((y, i, arr) => {
              const max = Math.max(...arr.map((x) => x.revenue));
              const h = max > 0 ? (y.revenue / max) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-(--accent)/40 rounded-t"
                    ref={(el) => { if (el) el.style.height = `${h}%`; }}
                  />
                  <div className="text-[9px] text-(--text-muted) mt-0.5">{y.year.slice(0, 4)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-[9px] text-(--text-muted) mt-2 text-right">
        Yahoo Finance · {new Date(f.fetchedAt).toLocaleString('zh-TW')}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = 'flat' }: { label: ReactNode; value: string; accent?: 'up' | 'down' | 'flat' }) {
  const cls = accent === 'up' ? 'text-(--up)' : accent === 'down' ? 'text-(--down)' : 'text-(--text-primary)';
  return (
    <div className="p-1.5 rounded bg-(--bg-base) border border-(--border)">
      <div className="text-[9px] text-(--text-muted)">{label}</div>
      <div className={`text-xs font-bold tnum ${cls}`}>{value}</div>
    </div>
  );
}

function fmtNum(n: number | undefined, digits = 2): string {
  if (n == null || isNaN(n)) return '—';
  return n.toFixed(digits);
}

function fmtPct(n: number | undefined): string {
  if (n == null || isNaN(n)) return '—';
  return `${n > 0 ? '+' : ''}${(n * 100).toFixed(1)}%`;
}

function fmtBig(n: number | undefined): string {
  if (n == null || isNaN(n) || n === 0) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  return n.toLocaleString();
}

function peAccent(pe?: number): 'up' | 'down' | 'flat' {
  if (pe == null) return 'flat';
  if (pe < 15) return 'up';  // 便宜
  if (pe > 30) return 'down'; // 偏貴
  return 'flat';
}

function marginAccent(m?: number, healthyAbove = 0.15): 'up' | 'down' | 'flat' {
  if (m == null) return 'flat';
  if (m >= healthyAbove) return 'up';
  if (m < 0) return 'down';
  return 'flat';
}

function growthAccent(g?: number): 'up' | 'down' | 'flat' {
  if (g == null) return 'flat';
  if (g > 0.10) return 'up';
  if (g < 0) return 'down';
  return 'flat';
}

function recommendationCls(mean?: number): string {
  if (mean == null) return 'border-(--border) text-(--text-muted)';
  if (mean <= 2) return 'border-(--up)/40 text-(--up)';
  if (mean >= 4) return 'border-(--down)/40 text-(--down)';
  return 'border-(--warn)/40 text-(--warn)';
}
